import React, { useId } from 'react';

import { buildBandLegendItems } from 'domain/surveyDataManager/map/buildRulesFromColorScheme';
import type { DefectVisualizationRule } from 'domain/surveyDataManager/contracts';
import { resolveDefectBandMeaningLabels } from 'domain/surveyDataManager/visualization/defectBandMeanings';
import type { ActiveDefectLayerId } from 'state/surveyDataManager/activeLayersSlice';

import { humanizeSlug } from '../../../utils/formatters';
import {
  formatWizardLanesLabel,
  isWizardVisualisationPresetActive,
  visualisationClassificationLabel,
  visualisationOwnershipLabel,
  WizardSelections,
  WizardVisualisationPreset,
} from '../wizardLogic';
import {
  filterLegendItemsToWorstCondition,
  isWorstConditionRuleset,
} from 'domain/surveyDataManager/visualization/worstConditionOnly';

export interface WizardStepVisualisationProps {
  readonly presets: readonly WizardVisualisationPreset[];
  readonly measurement: DefectVisualizationRule | null;
  readonly selections: WizardSelections;
  readonly activeLayerIds: ReadonlySet<ActiveDefectLayerId>;
  readonly isLoading: boolean;
  readonly loadErrorMessage: string | null;
  readonly displayName: string;
  readonly suggestedDisplayName: string;
  readonly onSelectPreset: (preset: WizardVisualisationPreset) => void;
  readonly onColourOverrideChange: (
    colourOverride: WizardSelections['colourOverride'],
  ) => void;
  readonly onDisplayNameChange: (displayName: string) => void;
}

function WizardStepVisualisation({
  presets,
  measurement,
  selections,
  activeLayerIds,
  isLoading,
  loadErrorMessage,
  displayName,
  suggestedDisplayName,
  onSelectPreset,
  onColourOverrideChange,
  onDisplayNameChange,
}: WizardStepVisualisationProps): JSX.Element {
  const displayNameId = useId();
  const colorLowId = useId();
  const colorHighId = useId();
  const legendItems = measurement
    ? buildBandLegendItems(
        measurement.colorScheme,
        resolveDefectBandMeaningLabels({
          surveyType: selections.surveyType,
          measurementSlug: String(measurement.slug),
          parameter: measurement.parameter,
          code: measurement.code,
          measurementName: measurement.name,
        }),
      )
    : [];

  const colourOverride = selections.colourOverride;
  const previewColors =
    colourOverride != null
      ? { min: colourOverride.colorLow, max: colourOverride.colorHigh }
      : measurement?.colorScheme ?? null;

  return (
    <div className="sdm-add-layer-wizard__panel">
      <h4 className="sdm-add-layer-wizard__panel-title">Choose visualisation</h4>
      <p className="sdm-add-layer-wizard__hint">
        Select a one-click visualisation for this parameter. Engineering thresholds are set on built-in recipes, not here.
      </p>

      {isLoading ? (
        <p className="sdm-add-layer-wizard__hint" role="status">
          Loading visualisation presets…
        </p>
      ) : null}

      {loadErrorMessage ? (
        <p className="sdm-add-layer-wizard__hint" role="alert">
          {loadErrorMessage} Showing the standard preset instead.
        </p>
      ) : null}

      <div className="sdm-add-layer-wizard__card-grid sdm-add-layer-wizard__card-grid--viz">
        {presets.map((preset) => {
          const isSelected = selections.visualisationPreset?.slug === preset.slug;
          const isDisabled = isWizardVisualisationPresetActive(
            activeLayerIds,
            selections,
            preset,
          );
          const swatchScheme =
            isSelected && previewColors ? previewColors : measurement?.colorScheme;
          const previewItems =
            swatchScheme != null
              ? buildBandLegendItems(
                  swatchScheme,
                  resolveDefectBandMeaningLabels({
                    surveyType: selections.surveyType,
                    measurementSlug: measurement ? String(measurement.slug) : '',
                    parameter: measurement?.parameter ?? '',
                    code: measurement?.code ?? '',
                    measurementName: measurement?.name ?? '',
                  }),
                )
              : legendItems;

          return (
            <button
              key={preset.id}
              type="button"
              className={`sdm-add-layer-wizard__card sdm-add-layer-wizard__card--viz${
                isSelected ? ' sdm-add-layer-wizard__card--selected' : ''
              }${isDisabled ? ' sdm-add-layer-wizard__card--disabled' : ''}`}
              onClick={() => onSelectPreset(preset)}
              disabled={isDisabled}
              aria-disabled={isDisabled || undefined}
              aria-pressed={isSelected}
            >
              <strong>
                {preset.name}
                {isDisabled ? ' (already active)' : ''}
              </strong>
              <p className="sdm-add-layer-wizard__card-description">{preset.description}</p>
              <span className="sdm-add-layer-wizard__card-meta">
                {visualisationOwnershipLabel(preset.ownership)} ·{' '}
                {visualisationClassificationLabel(preset.classificationType)}
              </span>
              {previewItems.length > 0 ? (
                <div
                  className="sdm-add-layer-wizard__preset-preview"
                  aria-hidden
                >
                  {(isWorstConditionRuleset(String(preset.slug))
                    ? filterLegendItemsToWorstCondition(previewItems)
                    : previewItems
                  ).map((item) => (
                    <span
                      key={`${preset.id}-${item.slug}`}
                      className="sdm-add-layer-wizard__preset-swatch"
                      style={{ background: item.color }}
                      title={item.label}
                    />
                  ))}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>

      {selections.visualisationPreset && colourOverride ? (
        <fieldset className="sdm-add-layer-wizard__quick-colours">
          <legend className="sdm-add-layer-wizard__field-label">
            Quick colours (optional)
          </legend>
          <p className="sdm-add-layer-wizard__hint">
            One-click ends for the selected preset. Leave as-is to use the standard
            scheme.
          </p>
          <div className="sdm-add-layer-wizard__quick-colours-row">
            <label className="sdm-add-layer-wizard__field" htmlFor={colorLowId}>
              <span className="sdm-add-layer-wizard__field-label">Low</span>
              <input
                id={colorLowId}
                type="color"
                value={colourOverride.colorLow}
                onChange={(event) =>
                  onColourOverrideChange({
                    colorLow: event.target.value,
                    colorHigh: colourOverride.colorHigh,
                  })
                }
              />
            </label>
            <label className="sdm-add-layer-wizard__field" htmlFor={colorHighId}>
              <span className="sdm-add-layer-wizard__field-label">High</span>
              <input
                id={colorHighId}
                type="color"
                value={colourOverride.colorHigh}
                onChange={(event) =>
                  onColourOverrideChange({
                    colorLow: colourOverride.colorLow,
                    colorHigh: event.target.value,
                  })
                }
              />
            </label>
          </div>
        </fieldset>
      ) : null}

      <label className="sdm-add-layer-wizard__field" htmlFor={displayNameId}>
        <span className="sdm-add-layer-wizard__field-label">Layer name</span>
        <input
          id={displayNameId}
          type="text"
          className="sdm-add-layer-wizard__search"
          value={displayName}
          placeholder={suggestedDisplayName}
          onChange={(event) => onDisplayNameChange(event.target.value)}
        />
      </label>

      <div className="sdm-add-layer-wizard__summary">
        <h5>Configuration summary</h5>
        <dl>
          <div>
            <dt>Survey type</dt>
            <dd>{humanizeSlug(selections.surveyType ?? '')}</dd>
          </div>
          <div>
            <dt>Parameter group</dt>
            <dd>{selections.defectGroup}</dd>
          </div>
          <div>
            <dt>Parameter</dt>
            <dd>{selections.measurement?.name}</dd>
          </div>
          <div>
            <dt>Year</dt>
            <dd>
              {selections.measurement?.surveyYears.find(
                (year) => year.slug === selections.surveyYearSlug,
              )?.surveyYear ?? selections.surveyYearSlug}
            </dd>
          </div>
          <div>
            <dt>Lane</dt>
            <dd>
              {formatWizardLanesLabel(
                selections.lanes,
                selections.measurement?.surveyYears.find(
                  (year) => year.slug === selections.surveyYearSlug,
                )?.lanes ?? selections.lanes,
              ) || 'Not selected'}
            </dd>
          </div>
          <div>
            <dt>Visualisation preset</dt>
            <dd>{selections.visualisationPreset?.name ?? 'Not selected'}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

export default WizardStepVisualisation;
